<template>
    <div>
        <b-modal
            id="modal-create-invoice"
            no-close-on-backdrop
            size="lg"
            :title="i18nT(`Add finance record from transaction`)"
        >

            <b-card no-body class="py-1 px-3">
                <b-row class="">
                    <b-col cols="12" class="text-center my-2 border-bottom">
                        <h4>{{i18nT(`Transaction details`)}}</h4>
                    </b-col>
                    <b-col cols="12">
                        <div class="d-flex">
                            <div class="transaction-detail mr-2">
                                <strong>{{i18nT(`Amount`)}}</strong>
                                <p :class="transaction.Amount >= 0 ? `text-success` : `text-danger`">{{ {amount: transaction.Amount, currency: transaction.Currency} | currency }}
                                </p>
                            </div>
                            <div class="transaction-detail mr-2">
                                <strong>{{i18nT(`Date`)}}</strong>
                                <p>
                                    {{transaction.TransactionDate | date}}
                                </p>
                            </div>
                            <div v-if="transaction.BankAccountObj" class="transaction-detail mr-2">
                                <strong>{{i18nT(`Bank`)}}</strong>
                                <p>
                                    {{transaction.BankAccountObj.Bank}}
                                </p>
                            </div>
                            <div v-if="transaction.BankAccountObj" class="transaction-detail mr-2">
                                <strong>{{i18nT(`Bank Account`)}}</strong>
                                <p class="mb-0">
                                    {{transaction.BankAccountObj.Label}}
                                </p>
                                <p class="font-small-3 text-muted">
                                    {{transaction.BankAccountObj.Iban}}
                                </p>
                            </div>

                            <div class="transaction-detail mr-2">
                                <strong>{{i18nT(`Transaction ID`)}}</strong>
                                <p class="text-truncate max-w-200">
                                    {{transaction.BankTransactionId}}
                                </p>
                            </div>



                        </div>
                        <div class="d-flex">
                            <div v-if="transaction.DebtorName" class="transaction-detail mr-2">
                                <strong>{{i18nT(`Debtor Name`)}}</strong>
                                <p>
                                    {{transaction.DebtorName}}
                                </p>
                            </div>
                            <div v-if="transaction.CreditorName" class="transaction-detail mr-2">
                                <strong>{{i18nT(`Creditor Name`)}}</strong>
                                <p>
                                    {{transaction.CreditorName}}
                                </p>
                            </div>
                            <div class="transaction-detail">
                                <strong>{{i18nT(`Description`)}}</strong>
                                <p>
                                    {{transaction.Label}}
                                </p>
                            </div>
                        </div>

                    </b-col>
                </b-row>
            </b-card>

            <b-card no-body class="py-1 px-3 mt-2">
                <b-row class="border-bottom pb-2">
                    <b-col cols="12">
                        <h4>{{ i18nT(`Attached invoice document`) }}</h4>
                    </b-col>
                    <b-col cols="12" v-if="!invoiceFile">
                        <b-row>
                            <b-col cols="12" md="4">
                                <b-form-checkbox
                                    v-model="dropzonePopulateFromResp"
                                    switch
                                    class="mb-2"
                                >
                                    {{ i18nT(`Automatically populate data from the uploaded invoice file`) }}
                                </b-form-checkbox>
                            </b-col>
                            <b-col cols="12" md="8" :class="dropzone1HasError ? 'sk-error' : ''">
                                <vue-dropzone
                                    ref="mainDropzone"
                                    :duplicateCheck="true"
                                    id="dz-transaction-invoice"
                                    class="sk-dropzone"
                                    :options="dropzoneOptions"
                                    @vdropzone-added="onFileAdded"
                                    @vdropzone-error="dropzoneError"
                                    @vdropzone-queue-complete="queComplete"
                                    @vdropzone-success="dropzoneSuccess"
                                    @vdropzone-sending="dropzoneAddParams"
                                />
                                <div class="mt-2">
                                    <span class="text-muted">
                                        {{ i18nT(`Here you can attach a scanned copy of the original document. The system will attempt to automatically recognize the data in the document and to populate the form below.`) }}
                                    </span>
                                </div>
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col cols="12" v-else>
                        <b-list-group-item tag="li">
                            <b-row>
                                <b-col cols="4" class="d-flex align-items-center">
                                    <b-link
                                        :href="invoiceFile.DownloadUrl || invoiceFile.Url"
                                        target="_blank"
                                        :download="invoiceFile.Label || invoiceFile.Name"
                                    >
                                        <b>{{ invoiceFile.Label || invoiceFile.Name }}</b>
                                    </b-link>
                                </b-col>
                                <b-col cols="6" class="d-flex align-items-center pt-1">
                                    <b-form-checkbox v-model="dropzonePopulateFromResp" switch>
                                        {{ i18nT(`Use uploaded invoice data`) }}
                                    </b-form-checkbox>
                                </b-col>
                                <b-col cols="2" class="d-flex align-items-center justify-content-end">
                                    <b-button variant="danger" @click="removePrimaryDocument">
                                        <feather-icon icon="Trash2Icon" size="16" />
                                    </b-button>
                                </b-col>
                            </b-row>
                        </b-list-group-item>
                    </b-col>
                </b-row>
            </b-card>

            <validation-observer ref="createInvoice">
                <b-col cols="12" class="text-center my-2 border-bottom">
                    <h4 v-if="documentType == 1">{{i18nT(`Income record details`)}}</h4>
                    <h4 v-if="documentType == 2">{{i18nT(`Expense record details`)}}</h4>
                </b-col>
                <b-row>
                    <b-col cols="6">
                        <b-form-group
                            :label="i18nT(`Category`)"
                            label-for="Category"
                            class="required"
                        >
                            <validation-provider
                                #default="{ errors }"
                                :name="i18nT(`Category`)"
                                rules="required"
                            >
                                <b-form-select
                                    v-model="paymentCategory"
                                    :options="paymentCategoryList"
                                    label="label"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="6" >
                        <b-form-group
                            :label="documentType == 1 ? i18nT(`Customer`) : i18nT(`Supplier`)"
                            label-for="Vendor"
                            class="required"
                        >
                            <validation-provider
                                #default="{ errors }"
                                :name="i18nT(`Vendor`)"
                                rules="required"
                            >
                                <div class="d-flex">
                                    <b-input-group-prepend>
                                        <b-button
                                            variant="primary"
                                            v-b-tooltip="i18nT(`Add new vendor`)"
                                            v-b-modal.add-vendor
                                            class="corners-straight-right"
                                        >
                                            <font-awesome-icon :icon="['fas', 'plus']" />
                                        </b-button>
                                    </b-input-group-prepend>
                                    <v-select
                                        class="corners-straight-left flex-grow-1"
                                        v-model="vendorId"
                                        :options="vendorList"
                                        label="text"
                                        :clearable="false"
                                        :reduce="option => option.value"
                                        :class="{ 'is-invalid': errors.length > 0 }"
                                    />
                                </div>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="6">
                        <b-form-group
                            :label="i18nT(`Document number`)"
                            label-for="Number"
                            class="required"
                        >
                            <validation-provider
                                #default="{ errors }"
                                :name="i18nT(`Document number`)"
                                rules="required"
                            >
                                <b-form-input
                                    type="text"
                                    v-model="invoiceNumber"
                                    :state="errors.length > 0 ? false:null"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>

                    <b-col
                        cols="6"
                    >
                        <b-form-group
                            :label="i18nT(`VAT (%)`)"
                            label-for="VatPercent"
                        >
                            <b-form-input
                                v-model="vatPercent"
                                type="number"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
            </validation-observer>


            <!-- Modal Footer -->
            <template #modal-footer>
                <div class="d-flex justify-content-end">
                    <b-button
                        variant="outline-secondary"
                        class="mr-2"
                        right
                        @click="cancel"
                    >
                        {{i18nT(`Cancel`)}}
                    </b-button>

                    <b-button
                        variant="primary"
                        class="mr-2"
                        right
                        @click="createInvoice(false)"
                    >
                        {{i18nT(`Save`)}}
                    </b-button>
                    <b-button
                        variant="primary"
                        right
                        @click="createInvoice(true)"
                    >
                        {{i18nT(`Save and Edit`)}}
                    </b-button>
                </div>
            </template>
        </b-modal>
        <b-modal id="add-vendor"
                :title="i18nT(`Add new vendor`)"
                 size="xl"
                 hide-footer
                 centered
                 :no-close-on-backdrop="true"
                 :no-close-on-esc="true"
        >
            <edit-vendor @vendorAdded="onVendorAdded" :modalMode="true" />
        </b-modal>
    </div>


</template>

<script>

import {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroupPrepend,
    BButton,
    BModal,
    BFormCheckbox,
    BListGroupItem,
    BLink,
    VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { dictToSelectArray } from '@core/utils/utils'
import useAuth from '@/auth/useAuth'
import EditVendor from '@/views/accounting/Vendors/EditVendor.vue'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import { API_KEY } from '@core/utils/constants'
import vSelect from 'vue-select'

export default {
    name: 'TransactionToFinance',
    components: {
        BCard,
        BRow,
        BCol,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BInputGroupPrepend,
        BButton,
        BModal,
        BFormCheckbox,
        BListGroupItem,
        BLink,
        ValidationProvider,
        ValidationObserver,
        ToastificationContent,
        EditVendor,
        VueDropzone: vue2Dropzone,
        vSelect
    },
    directives: {
        'b-tooltip': VBTooltip,
    },
    props: {
        transaction: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            vendorList: [],
            vendorId: null,

            paymentCategoryList: [],
            paymentCategory: 0,
            paymentCategoryId: null,

            vatPercent: 0,
            invoiceNumber: '',
            lastInvoiceNumber: '',

            documentType: 1,

            invoiceFile: null,
            dropzonePopulateFromResp: true,
            dropzone1HasError: false,
            dropzoneOptions: {
                url: process.env.VUE_APP_API_URL + 'invoices/invoiceFile',
                paramName: 'invoiceDocument',
                addRemoveLinks: true,
                autoProcessQueue: true,
                clickable: true,
                dictDefaultMessage: this.i18nT(`Drag and drop the invoice file here (PDF up to 5.0 MB)`),
                dictRemoveFile: this.i18nT(`Remove`),
                dictFileTooBig: this.i18nT(`File is too big ({{filesize}}MB). Max filesize is: {{maxFilesize}}MB`),
                dictInvalidFileType: this.i18nT(`Only PDFs are allowed`),
                dictCancelUpload: this.i18nT(`Cancel`),
                dictCancelUploadConfirmation: this.i18nT(`Are you sure you want to cancel this upload?`),
                dictMaxFilesExceeded: this.i18nT(`You can only upload 1 file here`),
                maxFilesize: 5,
                maxFiles: 1,
                acceptedFiles: 'application/pdf',
                headers: {
                    'Api-Key': API_KEY,
                    'Auth-Key': useAuth.getToken(),
                },
            },

            tempInvoice: null,
            originalForm: null,
            pendingVendorId: null,

            required,
            email,
        }
    },
    methods: {
        getVendorFilterQuery() {
            if (this.documentType === 1) {
                return '?clients=1'
            }
            return '?suppliers=1&client_suppliers=1'
        },
        fetchVendors() {
            const filter = this.getVendorFilterQuery()
            return this.$http.get(`vendors/vendorsList${filter}`).then(({ data }) => {
                this.vendorList = dictToSelectArray(data.data)
                this.applyPendingVendor()
                return this.vendorList
            })
        },
        applyPendingVendor() {
            if (!this.pendingVendorId) {
                return
            }
            const vendor = this.vendorList.find(vendorItem => `${vendorItem.value}` === `${this.pendingVendorId}`)
            if (vendor) {
                this.vendorId = vendor.value
                this.pendingVendorId = null
            }
        },
        onVendorAdded() {
            this.$bvModal.hide('add-vendor')
            this.fetchVendors().then(() => {
                if (this.vendorList.length) {
                    this.vendorId = this.vendorList[this.vendorList.length - 1].value
                }
            })
        },
        cancel() {
            this.resetForm()
            this.$refs.createInvoice.reset()
            this.$bvModal.hide('modal-create-invoice')
        },
        resetForm() {
            this.vendorId = null
            this.paymentCategory = 0
            this.paymentCategoryId = null
            this.vatPercent = 0
            this.invoiceNumber = ''
            this.invoiceFile = null
            this.tempInvoice = null
            this.originalForm = null
            this.pendingVendorId = null
            this.dropzonePopulateFromResp = true
            this.dropzone1HasError = false

            if (this.$refs.mainDropzone) {
                this.$refs.mainDropzone.removeAllFiles()
            }
        },
        createInvoice(saveAndEdit) {
            this.$refs.createInvoice.validate().then(success => {
                if (success) {
                    const formData = new FormData()
                    formData.append('transaction_id', this.transaction.Id)
                    formData.append('VendorId', this.vendorId)
                    formData.append('Category', this.paymentCategory)
                    formData.append('VatPercent', this.vatPercent)
                    formData.append('InvoiceNumber', this.invoiceNumber)

                    if (this.invoiceFile && this.invoiceFile.Id) {
                        formData.append('InvoiceFileId', this.invoiceFile.Id)
                    }

                    this.$http.post(`transactions/createInvoice`, formData).then((response) => {
                        this.$toast({
                            component: ToastificationContent,
                            props: {
                                title: this.successMessage(response),
                                icon: 'CheckIcon',
                                variant: 'success',
                                message: this.i18nT(`Record created successfully! Redirecting..`),
                            },
                        })
                        this.resetForm()
                        this.$refs.createInvoice.reset()
                        this.$bvModal.hide('modal-create-invoice')
                        this.$emit('refresh');

                        if (saveAndEdit) {
                            if (this.documentType === 1) {
                                const activeCategory = this.paymentCategoryList.find(category => category.value === this.paymentCategory)
                                if (activeCategory && activeCategory.fullData && activeCategory.fullData.Type === 1) {
                                    this.$router.push({ name: 'edit-invoice', params: { id: response.data.data.Id } })
                                } else {
                                    this.$router.push({ name: 'edit-income', params: { id: response.data.data.Id } })
                                }
                            } else if (this.documentType === 2) {
                                this.$router.push({ name: 'edit-expense', params: { id: response.data.data.Id } })
                            }
                        }
                    }).catch((error) => {
                        this.$toast({
                            component: ToastificationContent,
                            props: {
                                title: this.errorMessage(error),
                                icon: 'XIcon',
                                variant: 'danger',
                                message: this.i18nT(`Something went wrong!`),
                            },
                        })
                    })
                } else {
                    this.$toast({
                        component: ToastificationContent,
                        props: {
                            title: this.i18nT(`Please fill all required fields`),
                            icon: 'XIcon',
                            variant: 'danger',
                            message: this.i18nT(`Please fill all required fields`),
                        },
                    })
                }
            })
        },
        fetchCategories(docType) {
            this.$http.get(`invoices/categories?type=${docType}`).then(({ data }) => {
                this.paymentCategoryList = data.data.map(category => ({
                    value: category.Id,
                    text: category.Label,
                    fullData: category,
                }))
                if (docType === 1) {
                    const selectedCategory = this.paymentCategoryList.find(category => category.fullData.IsSystem === 1)

                    if (selectedCategory && selectedCategory.fullData) {
                        this.paymentCategory = selectedCategory.fullData.Id
                    }
                }
            })
        },
        dropzoneAddParams(file, xhr, formData) {
            const isClient = this.documentType === 2 ? 1 : 0
            formData.append('isClient', isClient)
            this.$store.commit('app/REQUEST_PENDING')
        },
        onFileAdded() {
            // Placeholder for future enhancements
        },
        queComplete() {
            if (this.$refs.mainDropzone) {
                this.$refs.mainDropzone.removeAllFiles()
            }
        },
        dropzoneError(message) {
            const self = this
            this.$store.commit('app/REQUEST_DONE')

            setTimeout(() => {
                if (self.$refs.mainDropzone) {
                    self.$refs.mainDropzone.removeAllFiles()
                }
                self.dropzone1HasError = false
            }, 6000)

            if (message && message.status === 'error' && !message.accepted) {
                self.dropzone1HasError = true
                const fallback = self.i18nT('File upload error')
                const messageText = message.previewElement && message.previewElement.childNodes && message.previewElement.childNodes[7]
                    ? message.previewElement.childNodes[7].innerText
                    : fallback
                self.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                        title: self.i18nT(messageText),
                        icon: 'InfoIcon',
                        variant: 'danger',
                    },
                })
            } else if (message && message.status === 'error' && message.accepted && message.xhr) {
                try {
                    const resp = JSON.parse(message.xhr.response)
                    if (resp && resp.message) {
                        self.$toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: resp.message,
                                icon: 'InfoIcon',
                                variant: 'danger',
                            },
                        })
                    }
                } catch (e) {
                    self.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: self.i18nT('Something went wrong!'),
                            icon: 'InfoIcon',
                            variant: 'danger',
                        },
                    })
                }
            }
        },
        dropzoneSuccess(file, response) {
            this.$store.commit('app/REQUEST_DONE')
            if (response && response.data && response.data.invoice) {
                const invoiceData = response.data.invoice
                this.invoiceFile = invoiceData.InvoiceFile || null
                this.tempInvoice = invoiceData
                this.pendingVendorId = invoiceData.VendorId ? `${invoiceData.VendorId}` : null
                this.dropzone1HasError = false

                if (!this.originalForm) {
                    this.originalForm = {
                        vendorId: this.vendorId,
                        paymentCategory: this.paymentCategory,
                        vatPercent: this.vatPercent,
                        invoiceNumber: this.invoiceNumber,
                    }
                }

                this.fetchVendors()

                if (this.dropzonePopulateFromResp) {
                    this.postProcessInvoice(invoiceData)
                }
            }
        },
        postProcessInvoice(invoiceData) {
            if (!invoiceData) {
                return
            }

            if (invoiceData.InvoiceNumber) {
                this.invoiceNumber = invoiceData.InvoiceNumber
            }

            if (typeof invoiceData.VatPercent !== 'undefined' && invoiceData.VatPercent !== null) {
                this.vatPercent = invoiceData.VatPercent
            }

            if (invoiceData.Category) {
                this.paymentCategory = invoiceData.Category
            }

            if (invoiceData.InvoiceFile) {
                this.invoiceFile = invoiceData.InvoiceFile
            }

            if (invoiceData.VendorId) {
                this.pendingVendorId = `${invoiceData.VendorId}`
                this.applyPendingVendor()
            }
        },
        restoreOriginalForm() {
            if (!this.originalForm) {
                return
            }
            this.vendorId = this.originalForm.vendorId
            this.paymentCategory = this.originalForm.paymentCategory
            this.vatPercent = this.originalForm.vatPercent
            this.invoiceNumber = this.originalForm.invoiceNumber
        },
        removePrimaryDocument() {
            this.invoiceFile = null
            this.tempInvoice = null
            this.pendingVendorId = null
            this.originalForm = null
            this.dropzonePopulateFromResp = true
            if (this.$refs.mainDropzone) {
                this.$refs.mainDropzone.removeAllFiles()
            }
        },
    },
    created() {
        if (this.transaction && typeof this.transaction.Amount !== 'undefined') {
            this.documentType = this.transaction.Amount >= 0 ? 1 : 2
        }

        this.$http.get('invoices/lastInvoiceNumber').then(({ data }) => {
            this.lastInvoiceNumber = data.data
        })

        const curWorkspace = useAuth.getCurWorkspace()
        this.vatPercent = curWorkspace.MainVatPercent
    },
    watch: {
        transaction: {
            immediate: true,
            deep: true,
            handler(newVal) {
                if (newVal && typeof newVal.Amount !== 'undefined') {
                    const docType = newVal.Amount >= 0 ? 1 : 2
                    if (this.documentType !== docType) {
                        this.documentType = docType
                    }
                }
            },
        },
        documentType: {
            immediate: true,
            handler(newVal) {
                this.fetchCategories(newVal)
                this.fetchVendors()
            },
        },
        dropzonePopulateFromResp(newVal) {
            if (newVal && this.tempInvoice) {
                this.postProcessInvoice(this.tempInvoice)
            } else if (!newVal) {
                this.restoreOriginalForm()
            }
        },
    },
}
</script>

<style scoped lang="scss">
.max-w-200 {
    max-width: 200px;
}
.plain-modal {
    background-color: var(--light-gray);
}

.discountIcon {
    position: relative;
    margin: 0 auto;
    margin-top: calc(50% - 1px);
}

.section-title {
    font-size: 11px;
}

.document-name {
    display: inline;
    line-height: 32px;
}

.document-actions-bar {
    float: right;
}
</style>
